import { updateAllHeaderCustomProperties } from '@theme/utilities';

// Suggests switching currency based on a one-time, client-side IP lookup,
// compared against the markets the merchant has actually enabled. Runs at
// most once per visitor ever (localStorage flag set before the network call
// even resolves) — never re-checks on repeat visits, never re-prompts after
// a dismiss or a switch. Fails silently: no geolocation, no match, or a
// merchant-disabled market all just mean the banner never appears.

const STORAGE_KEY = 'geo-currency-banner:checked';
const GEO_ENDPOINT = 'https://get.geojs.io/v1/ip/country.json';
const FETCH_TIMEOUT_MS = 4000;

function hasBeenChecked() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch (error) {
    // localStorage unavailable (private mode, disabled storage) — treat as
    // already-checked so we never retry on every single pageview.
    return true;
  }
}

function markChecked() {
  try {
    window.localStorage.setItem(STORAGE_KEY, '1');
  } catch (error) {
    /* ignore */
  }
}

function readData() {
  const script = document.getElementById('geo-currency-banner-data');
  if (!script) return null;
  try {
    return JSON.parse(script.textContent || '');
  } catch (error) {
    return null;
  }
}

function fetchDetectedCountry() {
  const controller = 'AbortController' in window ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null;

  return fetch(GEO_ENDPOINT, {
    credentials: 'omit',
    mode: 'cors',
    signal: controller ? controller.signal : undefined,
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((json) => (json && typeof json.country === 'string' ? json.country.toUpperCase() : null))
    .catch(() => null)
    .finally(() => {
      if (timeoutId) clearTimeout(timeoutId);
    });
}

function renderBanner(data, country) {
  const template = document.getElementById('geo-currency-banner-template');
  const headerGroup = document.getElementById('header-group');
  const formWrapper = document.getElementById('geo-currency-banner-form');
  if (!(template instanceof HTMLTemplateElement) || !headerGroup || !formWrapper) return;

  const fragment = template.content.cloneNode(true);
  const root = fragment.querySelector('[data-geo-currency-banner]');
  const messageEl = fragment.querySelector('[data-geo-currency-banner-message]');
  const switchBtn = fragment.querySelector('[data-geo-currency-banner-switch]');
  const dismissBtn = fragment.querySelector('[data-geo-currency-banner-dismiss]');
  if (!(root instanceof HTMLElement) || !messageEl || !switchBtn || !dismissBtn) return;

  const currencyLabel = `${country.currencyCode} ${country.currencySymbol}`.trim();
  messageEl.textContent = data.translations.message
    .replace('{{ country }}', country.name)
    .replace('{{ currency }}', currencyLabel);
  switchBtn.textContent = data.translations.switchCta.replace('{{ currency }}', country.currencyCode);

  switchBtn.addEventListener('click', () => {
    const form = formWrapper.querySelector('form.localization-form');
    const countryInput = formWrapper.querySelector('input[name="country_code"]');
    if (!(form instanceof HTMLFormElement) || !(countryInput instanceof HTMLInputElement)) return;
    countryInput.value = country.iso;
    form.submit(); // Full page reload — same behavior as assets/localization.js's own selectCountry().
  });

  dismissBtn.addEventListener('click', () => {
    root.remove();
    updateAllHeaderCustomProperties();
  });

  headerGroup.insertBefore(root, headerGroup.firstElementChild);
  updateAllHeaderCustomProperties();
}

(function init() {
  if (hasBeenChecked()) return;

  const data = readData();
  if (!data) return;

  // Mark as checked immediately, before the fetch resolves — guarantees
  // exactly one lookup attempt ever per visitor, even on failure/timeout.
  markChecked();

  fetchDetectedCountry().then((detectedIso) => {
    if (!detectedIso || detectedIso === data.currentCountry) return;
    const match = data.availableCountries.find((c) => c.iso === detectedIso);
    if (!match) return; // Merchant hasn't enabled this market — never offer it.
    renderBanner(data, match);
  });
})();
